import type { JSX } from 'react';

import type {
  AutomationPlanSource,
  AutomationSummaryProjection,
} from '@/lib/api/automations.js';

import { formatNextFire } from './automation-detail-labels.js';
import { cronHumanize } from './cron-humanize.js';

/** The fields that say when an automation runs next. */
export type NextRunFacts = Pick<
  AutomationSummaryProjection,
  'schedule' | 'scheduleText' | 'nextFireAt' | 'plan'
>;

/** Shown while the planner works out a planned automation's next run. */
export const PLANNING_LABEL = 'Working out the next run…';

/** Shown when the planner gave up on a planned automation's next run. */
export const STUCK_LABEL = "Couldn't work out the next run";

/** Shown beside a planned schedule whose dates a model picks, because no rule computes them. */
export const MODEL_DATES_NOTE =
  'A model chooses the dates of this schedule, and can occasionally pick a wrong one.';

/** Who decided the next run, in the user's terms. */
const PLAN_SOURCE_LABELS: Record<AutomationPlanSource, string> = {
  run: 'Planned by the last run',
  creation: 'Planned when the schedule was saved',
  planner: 'Planned from the schedule',
  user: 'Planned by you',
  rule: 'Computed from the schedule',
};

/** The source line, or null when the server named no source this build knows. */
function planSourceLabel(source: AutomationPlanSource | null): string | null {
  return source === null ? null : PLAN_SOURCE_LABELS[source];
}

/** True when a planned automation's chain stopped and needs the user. */
export function needsAttention(facts: NextRunFacts): boolean {
  return facts.schedule.type === 'planned' && facts.plan?.status === 'stuck';
}

/** The list row's schedule: the words, led by the date when a planned run is decided. */
export function scheduleSummary(facts: NextRunFacts): string {
  const words = cronHumanize(facts.schedule, facts.scheduleText);
  if (facts.schedule.type !== 'planned') return words;
  if (facts.plan?.status === 'planning') return PLANNING_LABEL;
  if (facts.plan?.status === 'stuck' || !facts.nextFireAt) return words;
  return `${formatNextFire(facts.nextFireAt)} · ${words}`;
}

/**
 * The detail page's "Next run": when, the schedule in words, and for a planned run why and who
 * decided, closed by the model note when no rule computes the dates.
 */
export function NextRunLines({
  facts,
  showWords = true,
  showRecovery = true,
}: {
  facts: NextRunFacts;
  /** Off where the page already shows the schedule in words next to this. */
  showWords?: boolean;
  /** Off where the reader can neither edit the schedule nor fire the automation. */
  showRecovery?: boolean;
}): JSX.Element {
  const modelPicksDates =
    facts.schedule.type === 'planned' && !facts.schedule.rule;
  return (
    <>
      <NextRunState
        facts={facts}
        showWords={showWords}
        showRecovery={showRecovery}
      />
      {modelPicksDates && <MutedLine text={MODEL_DATES_NOTE} />}
    </>
  );
}

/** Where the next run stands: decided, being worked out, or stuck. */
function NextRunState({
  facts,
  showWords,
  showRecovery,
}: {
  facts: NextRunFacts;
  showWords: boolean;
  showRecovery: boolean;
}): JSX.Element {
  const words = cronHumanize(facts.schedule, facts.scheduleText);
  const plan = facts.schedule.type === 'planned' ? facts.plan : null;
  if (plan?.status === 'stuck') {
    return (
      <>
        <span className="text-sm text-foreground">{STUCK_LABEL}</span>
        {plan.reason && <MutedLine text={plan.reason} />}
        {showRecovery && (
          <MutedLine text="Edit the schedule or use Fire now to try again." />
        )}
        {showWords && <MutedLine text={words} />}
      </>
    );
  }
  if (plan?.status === 'planning') {
    return (
      <>
        <span className="text-sm text-foreground">{PLANNING_LABEL}</span>
        {showWords && <MutedLine text={words} />}
      </>
    );
  }
  const source = plan ? planSourceLabel(plan.source) : null;
  return (
    <>
      <span className="text-sm text-foreground">
        {formatNextFire(facts.nextFireAt)}
      </span>
      {showWords && <MutedLine text={words} />}
      {plan?.reason && plan.source !== 'rule' && (
        <MutedLine text={`Why: ${plan.reason}`} />
      )}
      {source && <MutedLine text={source} />}
    </>
  );
}

function MutedLine({ text }: { text: string }): JSX.Element {
  return <span className="text-xs text-muted-foreground">{text}</span>;
}
